import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useToast } from '../../contexts/ToastContext';
import type { Notification, NotificationType } from '../../types/notification';

/** Notification types that should surface an in-app toast when they arrive in real time. */
const TOAST_TYPES = new Set<NotificationType>([
  'new_request',
  'routemize_request',
  'routemize_appointment',
  'new_appointment',
  // No customerName in its metadata, so the toast shows the description:
  // "{CallerName} (...) left a message with the AI voice agent: ... call back at ...".
  'voice_message',
]);

const buildToastMessage = (notification: Notification): string => {
  const customer = notification.metadata?.customerName;
  if (customer) {
    return `${notification.title} — ${customer}`;
  }
  return notification.message || notification.title;
};

/**
 * Shows a toast popup when new request/appointment notifications arrive via SignalR.
 * Mounted inside the authenticated shell so team members are alerted without
 * opening the notifications panel.
 */
const NotificationToastBridge: React.FC = () => {
  const { notifications, hasLoaded } = useNotifications();
  const { info, warning } = useToast();
  const location = useLocation();
  const knownIdsRef = useRef<Set<string> | null>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (location.pathname.startsWith('/notifications')) {
      return;
    }

    // Wait for the first fetch to settle — even when the result is empty.
    // Seeding on the empty pre-fetch render would make the entire first page
    // look like fresh arrivals; skipping empty loads entirely would swallow
    // the first real notification after an empty inbox (it would be treated
    // as the baseline instead of toasted).
    if (!hasLoaded) {
      return;
    }

    const currentIds = new Set(notifications.map((n) => n.id));

    if (!hasInitializedRef.current) {
      // First settled load (including an empty inbox) is the baseline.
      // Only notifications that arrive AFTER this point should surface a toast.
      knownIdsRef.current = currentIds;
      hasInitializedRef.current = true;
      return;
    }

    const previousIds = knownIdsRef.current ?? new Set<string>();

    for (const notification of notifications) {
      if (previousIds.has(notification.id)) continue;
      if (notification.isRead) continue;

      // System announcements always toast (that's the point of the live push);
      // other notifications only toast for the request/appointment types.
      const isAnnouncement = notification.source === 'announcement';
      if (!isAnnouncement && !TOAST_TYPES.has(notification.type)) continue;

      const message = buildToastMessage(notification);
      // Announcements don't auto-dismiss (duration 0) — the user closes them
      // manually so a system-wide notice isn't missed.
      if (isAnnouncement && notification.priority === 'high') {
        warning(message, 0);
      } else if (isAnnouncement) {
        info(message, 0);
      } else {
        info(message, 8000);
      }
    }

    knownIdsRef.current = currentIds;
  }, [notifications, location.pathname, info, hasLoaded]);

  return null;
};

export default NotificationToastBridge;
